import { Select as ControlledSelect, Input, Checkbox } from '@/components/Form'
import React, { useCallback } from 'react'
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form'

import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { OfferType } from '@/features/offers/constants'
import useUser from '@/features/users/useUser'
import { camelToWords } from '@/utils/stringHelper'
import { Box, Button, Flex, Heading, VStack } from '@chakra-ui/react'
import CostCenterUserTable from './CostCenterUserTable'

const CostCenterForm = ({ costCenter, onSubmitAction }) => {
  const methods = useForm({ defaultValues: costCenter })

  const { append, remove } = useFieldArray({ control: methods.control, name: 'approvers' })

  const approvers = useWatch({ name: 'approvers', control: methods.control })
  const { useUserOptions, useFetchAllUsers } = useUser()

  //translated
  const { data: allUserResponse } = useFetchAllUsers({})
  const { data: procurementUserOptions } = useUserOptions({
    valueKey: '_id',
    contributorGroup: ContributorGroupType.PROCUREMENT_OFFICER,
  })
  const { data: teamAssistantUserOptions } = useUserOptions({
    valueKey: '_id',
    contributorGroup: ContributorGroupType.TEAM_ASSISTANT,
  })

  const handleAddRow = useCallback(() => {
    append({
      type: '',
      level: 3,
      user: {
        _id: '',
        name: '',
      },
    })
  }, [append])

  const handleUpdateTableData = (rowIndex, columnId, value) => {
    methods.setValue(`approvers.${rowIndex}.${columnId}`, value)

    if (columnId === 'user._id') {
      // Also update the email
      const selectedUser = allUserResponse.data.find((user) => user._id === value)
      if (selectedUser) {
        methods.setValue(`approvers.${rowIndex}.user.email`, selectedUser?.email)
      }
    }
  }

  return (
    <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmitAction)}>
          <Flex p={5} borderBottom="1px solid #d8dbe0">
            <Heading> Cost Center: {costCenter.name} </Heading>
          </Flex>
          <Box p={5}>
            <VStack mb="5">
              <Input name="code" label="Code" type="text" placeholder="Enter value" isRow />
              <Input name="profitCenterCode" label="Profit Center Code" type="text" placeholder="Enter value" isRow />
              <Input name="quotationCode" label="Quotation Code" type="text" placeholder="Enter value" isRow />
              <Input name="name" label="Name" type="text" placeholder="Enter value" isRow />
              <Input name="description" label="Description" type="text" placeholder="Enter value" isRow />
              <Checkbox name="workInstructionAllowed" label="Work Instruction Allowed" isRow />
              <ControlledSelect
                name="type"
                label="Type"
                options={[
                  { value: '', label: '' },
                  { value: OfferType.MAINTENANCE, label: camelToWords(OfferType.MAINTENANCE) },
                  { value: OfferType.PROJECT, label: camelToWords(OfferType.PROJECT) },
                ]}
                isRow
                isRequired={false}
              />
              <ControlledSelect
                name="defaultProcurementOfficer"
                label="Default Procurement Officer"
                options={procurementUserOptions}
                isRow
                isRequired={false}
              />
              <ControlledSelect
                name="defaultTeamAssistant"
                label="Default Team Assistant"
                options={teamAssistantUserOptions}
                isRow
                isRequired={false}
              />
            </VStack>
            <Flex direction={'column'} gap="3">
              <Flex justifyContent="end">
                <Button colorScheme="green" ml="auto" onClick={handleAddRow}>
                  Add
                </Button>
              </Flex>
              <CostCenterUserTable data={approvers} handleUpdateData={handleUpdateTableData} removeUser={remove} />
            </Flex>

            <Button mt="6" colorScheme="blue" type="submit">
              Save
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Box>
  )
}

export default CostCenterForm
